import { Link } from "react-router-dom";
import { Mail, MapPin, Phone, ShieldCheck, Users } from "lucide-react";
import NewsletterSignup from "@/components/NewsletterSignup";
import Downloads from "@/components/Downloads";

export default function FoundationFooter() {
  return (
    <footer data-testid="foundation-footer" className="bg-black border-t border-white/10 mt-24">
      {/* Newsletter capture */}
      <div className="border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-14 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-6">
            <p className="overline text-white/50">Stay Connected</p>
            <p className="font-serif text-3xl md:text-4xl font-light text-white mt-3 leading-tight">
              Follow the journey. <em className="italic">Be part of the impact.</em>
            </p>
            <p className="text-white/55 font-light mt-4 max-w-md">
              Join our list for stories, drives, and ways to help mothers, children, and youth in our community.
            </p>
          </div>
          <div className="md:col-span-6 md:flex md:justify-end">
            <NewsletterSignup />
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 grid grid-cols-1 md:grid-cols-12 gap-12">
        {/* Foundation identity */}
        <div className="md:col-span-5">
          <Link to="/foundation" data-testid="foundation-footer-logo" className="group inline-flex items-center">
            <img
              src="/brand/tandemt-foundation-logo.png"
              alt="The Tandem T Foundation Inc"
              className="h-40 md:h-48 w-auto object-contain transition-opacity duration-300 group-hover:opacity-90"
              style={{ mixBlendMode: "screen" }}
            />
          </Link>
          <p className="font-serif italic text-2xl md:text-3xl font-light leading-tight mt-8 max-w-md text-white/90">
            &ldquo;Whatever the chapter, you don&rsquo;t have to face it alone.&rdquo;
          </p>
          <p className="text-sm text-white/55 font-light mt-6 max-w-md">
            Tandem T Foundation is a federally recognized 501(c)(3) nonprofit organization.
            Contributions are tax-deductible to the extent permitted by law.
          </p>
          <div className="mt-8 flex flex-wrap gap-2">
            <span className="overline px-3 py-2 border border-white/20 text-white/80">501(c)(3) Nonprofit</span>
            <span className="overline px-3 py-2 border border-white/20 text-white/80">Tax-Deductible</span>
            <span className="overline px-3 py-2 border border-white/20 text-white/80">Community Partnership</span>
          </div>
          <a
            href="https://app.candid.org/profile/16755761/the-tandem-t-foundation-inc/?pkId=cd9cbaf7-c4c4-4b9c-a970-8f76b4543f65&isActive=true"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-candid-seal"
            className="mt-6 inline-flex items-center gap-3 px-4 py-3 border border-white/20 rounded-lg hover:bg-white/5 transition-colors"
          >
            <ShieldCheck size={18} className="text-white/80" />
            <span className="text-left leading-tight">
              <span className="block overline text-[10px] text-white/45">Verified Transparency</span>
              <span className="block text-sm text-white/85">Candid Nonprofit Profile</span>
            </span>
          </a>
          <Downloads className="mt-8" />
        </div>

        {/* Foundation nav */}
        <div className="md:col-span-3">
          <p className="overline text-white/50 mb-5">Foundation</p>
          <ul className="space-y-3 text-sm">
            <li><Link to="/foundation" data-testid="foundation-footer-home" className="text-white/80 hover:text-white">Home</Link></li>
            <li><Link to="/foundation/our-story" data-testid="foundation-footer-our-story" className="text-white/80 hover:text-white">Our Story</Link></li>
            <li><Link to="/foundation/team" data-testid="foundation-footer-team" className="text-white/80 hover:text-white">Our Team</Link></li>
            <li><Link to="/foundation/updates" data-testid="foundation-footer-updates" className="text-white/80 hover:text-white">Updates &amp; Impact</Link></li>
            <li><Link to="/foundation/new-beginnings#partner-form-section" data-testid="foundation-footer-partner" className="text-white/80 hover:text-white">Become a Partner</Link></li>
            <li><Link to="/foundation/contact?intent=volunteer" data-testid="foundation-footer-volunteer" className="text-white/80 hover:text-white">Volunteer</Link></li>
            <li><Link to="/foundation/contact" data-testid="foundation-footer-contact" className="text-white/80 hover:text-white">Contact</Link></li>
            <li><Link to="/foundation/donate" data-testid="foundation-footer-give" className="text-white/80 hover:text-white">Give</Link></li>
            <li><a href="https://www.paypal.com/US/fundraiser/charity/6244251" target="_blank" rel="noopener noreferrer" data-testid="foundation-footer-paypal-giving-fund" className="text-white/80 hover:text-white">PayPal Giving Fund</a></li>
          </ul>
        </div>

        {/* Foundation contact */}
        <div className="md:col-span-4">
          <p className="overline text-white/50 mb-5">Foundation Contact</p>
          <ul className="space-y-4 text-sm text-white/80">
            <li className="flex items-start gap-3">
              <Users size={14} className="mt-1 shrink-0" />
              <div data-testid="foundation-footer-leadership">
                <p className="overline text-[10px] text-white/45">Leadership</p>
                <p className="mt-1">Chrissy Thompson <span className="text-white/55">— Founder &amp; President</span></p>
                <p className="mt-0.5">Rohan Taylor <span className="text-white/55">— Co-Founder &amp; Vice President</span></p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <Mail size={14} className="mt-1 shrink-0" />
              <a href="mailto:partners@tandemtfoundation.org" data-testid="foundation-footer-email" className="hover:text-white">
                partners@tandemtfoundation.org
              </a>
            </li>
            <li className="flex items-start gap-3">
              <Phone size={14} className="mt-1 shrink-0" />
              <a href="tel:+16562566532" data-testid="foundation-footer-phone" className="hover:text-white">
                656-256-6532
              </a>
            </li>
            <li className="flex items-start gap-3">
              <MapPin size={14} className="mt-1 shrink-0" />
              <div data-testid="foundation-footer-address">
                <p className="mt-0">550 N Reo St, Suite 300, Tampa, FL 33609</p>
                <p className="text-white/55 mt-1 leading-relaxed">
                  Proudly serving Apollo Beach, South Shore &amp; Greater Hillsborough County — building
                  partnerships across Florida.
                </p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <ShieldCheck size={14} className="mt-1 shrink-0" />
              <div>
                <p className="overline text-[10px] text-white/45">Transparency</p>
                <p className="mt-1">The Tandem T Foundation Inc</p>
                <p className="text-white/60">Federally recognized 501(c)(3) · EIN 42-3781424</p>
              </div>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-6">
          <p data-testid="footer-501c3" className="text-xs text-white/45 font-light leading-relaxed max-w-4xl">
            The Tandem T Foundation Inc is a federally recognized 501(c)(3) nonprofit organization.
          </p>
          <div className="mt-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-xs text-white/40">
            <p>© {new Date().getFullYear()} The Tandem T Foundation Inc. All rights reserved.</p>
            <p className="font-serif italic">Service. Community. Legacy.</p>
          </div>
        </div>
      </div>
    </footer>
  );
}
